import React from 'react';
import i18n from 'i18n';
import { connect } from 'react-redux';
import ApplicationState from 'store/interfaces';
import Grid from '@material-ui/core/Grid';
import { Typography, makeStyles } from '@material-ui/core';
import { PeriodCountAccess } from 'modules/monitor/interfaces';
import AutoCard from 'components/AutoCard';

import {
  setPartnerDataPeriodCardFilter,
  getDataDashboardHistoryRequest,
} from 'modules/dashboard/actions';
import FilterSelector from 'components/FilterSelector';
import { formatPercent } from 'utils/helpers';
import StyledTypography from 'components/StyledTypography';
import { DashboardDataCompanyFields } from 'modules/dashboard/interfaces';
import { CompanyData, PartnerData } from 'modules/authenticate/interfaces';

import styles, { overrideCard, overrideFilter } from './styles';

interface Props {
  companyDashboardData: DashboardDataCompanyFields;
  companySelected: CompanyData | null;
  intervalTime: any;
  partnerSelected: PartnerData | null;
  getDataDashboardHistoryRequest: ({
    companyUuid,
    partnerUuid,
    intervalTime,
  }) => void;
  setPartnerDataPeriodCardFilter: (prop: string) => void;
}

const ClientsHistory = ({
  companyDashboardData,
  companySelected,
  intervalTime = '30d',
  partnerSelected,
  getDataDashboardHistoryRequest,
  setPartnerDataPeriodCardFilter,
}: Props) => {
  const useStyles = makeStyles(styles);
  const classes = useStyles({});

  const filterPeriodOptions = () => [
    {
      id: 'last-7-days',
      label: i18n.t(
        `ENVIRONMENT_DASHBOARD.LAST_${PeriodCountAccess.LAST_7_DAYS.toUpperCase()}`
      ),
      onClick: () =>
        setPartnerDataPeriodCardFilter(PeriodCountAccess.LAST_7_DAYS),
    },
    {
      id: 'last-15-days',
      label: i18n.t(
        `ENVIRONMENT_DASHBOARD.LAST_${PeriodCountAccess.LAST_15_DAYS.toUpperCase()}`
      ),
      onClick: () =>
        setPartnerDataPeriodCardFilter(PeriodCountAccess.LAST_15_DAYS),
    },
    {
      id: 'last-30-days',
      label: i18n.t(
        `ENVIRONMENT_DASHBOARD.LAST_${PeriodCountAccess.LAST_30_DAYS.toUpperCase()}`
      ),
      onClick: () =>
        setPartnerDataPeriodCardFilter(PeriodCountAccess.LAST_30_DAYS),
    },
    {
      id: 'last-60-days',
      label: i18n.t(
        `ENVIRONMENT_DASHBOARD.LAST_${PeriodCountAccess.LAST_60_DAYS.toUpperCase()}`
      ),
      onClick: () =>
        setPartnerDataPeriodCardFilter(PeriodCountAccess.LAST_60_DAYS),
    },
    {
      id: 'last-90-days',
      label: i18n.t(
        `ENVIRONMENT_DASHBOARD.LAST_${PeriodCountAccess.LAST_90_DAYS.toUpperCase()}`
      ),
      onClick: () =>
        setPartnerDataPeriodCardFilter(PeriodCountAccess.LAST_90_DAYS),
    },
    {
      id: 'last-180-days',
      label: i18n.t(
        `ENVIRONMENT_DASHBOARD.LAST_${PeriodCountAccess.LAST_180_DAYS.toUpperCase()}`
      ),
      onClick: () =>
        setPartnerDataPeriodCardFilter(PeriodCountAccess.LAST_180_DAYS),
    },
  ];

  React.useEffect(() => {
    if (companySelected && partnerSelected) {
      getDataDashboardHistoryRequest({
        companyUuid: companySelected.id,
        partnerUuid: partnerSelected.id,
        intervalTime,
      });
    }
  }, [
    companySelected,
    getDataDashboardHistoryRequest,
    intervalTime,
    partnerSelected,
  ]);

  return (
    <>
      <Grid container justify="space-between">
        <Grid item xs={6}>
          <Typography className={classes.titleCard}>
            {i18n.t('HISTORY_SOP_KEY')}
            {':'}
          </Typography>
        </Grid>
        <Grid item xs={6} className={classes.filter}>
          <FilterSelector
            overrideClasses={overrideFilter}
            options={filterPeriodOptions()}
            currentValue={{
              label: i18n.t(
                `ENVIRONMENT_DASHBOARD.LAST_${intervalTime.toUpperCase()}`
              ),
              onClick: () => null,
            }}
          />
        </Grid>
      </Grid>
      <AutoCard overrideClasses={overrideCard()}>
        <Grid item justify="flex-start" className={classes.values}>
          <Grid item xs={10} className={classes.content}>
            <Grid container alignItems="center" justify="space-between">
              <Grid item>
                <Typography className={classes.title}>
                  {i18n.t('CREATED_CLIENTS_KEY')}
                </Typography>
              </Grid>

              <Grid item>
                <StyledTypography
                  value={
                    companyDashboardData?.summary?.created_clients_in_period
                      ?.count
                  }
                />
              </Grid>
            </Grid>

            <Typography className={classes.subtitle}>
              {i18n.t('CREATED_CLIENTS_BY_PERIOD_KEY', {
                percentage: formatPercent(
                  companyDashboardData?.summary?.created_clients_in_period
                    ?.growth_in_period_percent
                ),
              })}
            </Typography>
          </Grid>
          <Grid item xs={10} className={classes.content}>
            <Grid container alignItems="center" justify="space-between">
              <Grid item>
                <Typography className={classes.title}>
                  {i18n.t('CREATED_USERS_KEY')}
                </Typography>
              </Grid>

              <Grid item>
                <StyledTypography
                  value={
                    companyDashboardData?.summary?.created_users_in_period
                      ?.count
                  }
                />
              </Grid>
            </Grid>

            <Typography className={classes.subtitle}>
              {i18n.t('CREATED_CLIENTS_BY_PERIOD_KEY', {
                percentage: formatPercent(
                  companyDashboardData?.summary?.created_users_in_period
                    ?.growth_in_period_percent
                ),
              })}
            </Typography>
          </Grid>
        </Grid>
      </AutoCard>
    </>
  );
};

const mapStateToProps = ({
  dashboard: { companyDashboardData, periodFilterPartner },
  authenticate: { companySelected, partnerSelected },
}: ApplicationState) => ({
  companyDashboardData,
  companySelected,
  partnerSelected,
  intervalTime: periodFilterPartner,
});

export default connect(mapStateToProps, {
  setPartnerDataPeriodCardFilter,
  getDataDashboardHistoryRequest,
})(ClientsHistory);
